import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  AlertTriangle,
  Target,
  User,
  TrendingUp,
  MessageSquare,
  Mail,
  Brain,
  Shield,
  Clock,
  DollarSign,
  Users,
  AlertCircle,
  ChevronRight,
  Sparkles,
  FileText,
  Zap,
  HelpCircle,
  Archive,
  Building,
  Activity,
  Radar,
  Lock,
  Pencil,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Report() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { hasFeature, plan } = useSubscription();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [editingDealValue, setEditingDealValue] = useState(false);
  const [dealValueInput, setDealValueInput] = useState('');
  const [linkingDeal, setLinkingDeal] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [driversOpen, setDriversOpen] = useState(false);
  const [activeSignal, setActiveSignal] = useState(null);
  const [taskRecs, setTaskRecs] = useState([]);
  const [creatingTask, setCreatingTask] = useState(null);
  const transcriptRef = useRef(null);

  // Auto-scroll to highlighted signal in transcript
  useEffect(() => {
    if (activeSignal) {
      setTimeout(() => {
        const mark = document.querySelector('[data-testid="signals-transcript-panel"] mark');
        if (mark) {
          const viewport = mark.closest('[data-radix-scroll-area-viewport]');
          if (viewport) {
            const markRect = mark.getBoundingClientRect();
            const viewportRect = viewport.getBoundingClientRect();
            viewport.scrollTop += markRect.top - viewportRect.top - viewportRect.height / 3;
          }
        }
      }, 200);
    }
  }, [activeSignal]);

  useEffect(() => {
    fetchReport();
    fetchInsights();
    fetchTaskRecs();
  }, [callId]);

  const fetchReport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calls/${callId}/analysis`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const reportData = await response.json();
      setData(reportData);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calls/${callId}/smart-insights`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        if (d.insights) {
          setInsights(d.insights);
          return;
        }
      }
      // No cached insights — auto-generate
      autoGenerateInsights();
    } catch (e) { console.error(e); }
  };

  const autoGenerateInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await fetch(`${API_URL}/api/calls/${callId}/smart-insights`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        if (d.insights) setInsights(d.insights);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingInsights(false); }
  };

  const fetchTaskRecs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/recommend/${callId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) setTaskRecs(await res.json());
    } catch (e) { console.error(e); }
  };

  const acceptTask = async (rec) => {
    setCreatingTask(rec.title);
    try {
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: rec.title, company: rec.company, call_id: rec.call_id, due_date: rec.due_date, priority: rec.priority })
      });
      if (res.ok) {
        toast.success('Task created');
        setTaskRecs(prev => prev.filter(r => r.title !== rec.title));
      }
    } catch (e) { toast.error('Failed'); }
    finally { setCreatingTask(null); }
  };

  const generateInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await fetch(`${API_URL}/api/calls/${callId}/smart-insights`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const d = await res.json();
        if (d.insights) setInsights(d.insights);
        toast.success('Insights generated');
      }
    } catch (e) { toast.error('Failed to generate insights'); }
    finally { setLoadingInsights(false); }
  };

  const copyEmail = () => {
    if (data?.analysis?.follow_up_email_draft) {
      navigator.clipboard.writeText(data.analysis.follow_up_email_draft);
      setCopied(true);
      toast.success('Email copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleArchive = async (category) => {
    setArchiving(true);
    try {
      const response = await fetch(`${API_URL}/api/calls/${callId}/archive`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archive_category: category })
      });
      if (response.ok) {
        toast.success(`Call archived as ${category}`);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to archive call');
    } finally {
      setArchiving(false);
    }
  };

  const saveDealValue = async () => {
    const amount = dealValueInput ? parseFloat(dealValueInput.replace(/,/g, '')) : null;
    if (dealValueInput && isNaN(amount)) {
      toast.error('Please enter a valid number');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/calls/${callId}/deal-value`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, currency: 'USD' }),
      });
      if (res.ok) {
        const result = await res.json();
        setData(prev => ({
          ...prev,
          analysis: {
            ...prev.analysis,
            estimated_deal_value: result.estimated_deal_value,
          }
        }));
        toast.success('Deal value updated');
        setEditingDealValue(false);
      }
    } catch {
      toast.error('Failed to update deal value');
    }
  };

  const linkToDeal = async () => {
    setLinkingDeal(true);
    try {
      // Try to find an existing deal for this company, or create one
      const listRes = await fetch(`${API_URL}/api/deals`, { credentials: 'include' });
      const listData = listRes.ok ? await listRes.json() : { deals: [] };
      const companyName = data?.call?.company_name;
      let existingDeal = listData.deals?.find(d => d.company_name?.toLowerCase() === companyName?.toLowerCase());

      if (!existingDeal) {
        // Create a new deal
        const createRes = await fetch(`${API_URL}/api/deals`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_name: companyName || 'Unknown',
            deal_name: `${companyName || 'Unknown'} Deal`,
            deal_stage: data?.call?.deal_stage || 'Discovery',
            call_ids: [callId],
          }),
        });
        if (createRes.ok) {
          existingDeal = await createRes.json();
          toast.success('Deal created and call linked');
        }
      } else {
        // Link call to existing deal
        await fetch(`${API_URL}/api/deals/${existingDeal.deal_id}/link`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_id: callId }),
        });
        toast.success(`Linked to ${existingDeal.deal_name}`);
      }

      if (existingDeal) {
        navigate(`/deal/${existingDeal.deal_id}`);
      }
    } catch {
      toast.error('Failed to link to deal');
    } finally {
      setLinkingDeal(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this call? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/calls/${callId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Call deleted');
        navigate('/dashboard');
      } else {
        toast.error('Failed to delete call');
      }
    } catch {
      toast.error('Failed to delete call');
    } finally {
      setDeleting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-success';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  // Win Probability conversion from raw deal score
  const getWinProbability = (score) => {
    if (score >= 90) return { pct: Math.round(90 + (score - 90) * 1), label: 'Very High', color: 'text-emerald-600', bg: 'bg-emerald-500/15 border-emerald-500/30' };
    if (score >= 75) return { pct: Math.round(70 + (score - 75) / 15 * 20), label: 'High', color: 'text-success', bg: 'bg-success/15 border-success/30' };
    if (score >= 60) return { pct: Math.round(50 + (score - 60) / 15 * 20), label: 'Promising', color: 'text-amber-600', bg: 'bg-yellow-500/15 border-yellow-500/30' };
    if (score >= 40) return { pct: Math.round(30 + (score - 40) / 20 * 20), label: 'Medium', color: 'text-orange-600', bg: 'bg-orange-500/15 border-orange-500/30' };
    return { pct: Math.round(10 + score / 40 * 20), label: 'Low', color: 'text-destructive', bg: 'bg-destructive/15 border-destructive/30' };
  };

  // Build win probability explanation - balanced positive/negative
  const getWinFactors = () => {
    const pos = insights?.why_this_probability?.positive_drivers || [];
    const neg = [
      ...(insights?.why_this_probability?.negative_drivers || []),
      ...(insights?.why_this_probability?.missing_elements || [])
    ];
    const factors = [];
    pos.slice(0, 2).forEach(d => factors.push({ positive: true, text: d }));
    neg.slice(0, 2).forEach(d => factors.push({ positive: false, text: d }));
    return factors;
  };

  // Fuzzy match: find the best matching substring in transcript for a signal quote
  const findBestMatch = (transcript, quote) => {
    if (!transcript || !quote) return null;
    const tLower = transcript.toLowerCase();
    // Clean quote: remove trailing dots, prefix labels like "Competitor referenced:"
    let qLower = quote.toLowerCase()
      .replace(/^[^:]+:\s*["']?/, '') // strip "Label: " prefix
      .replace(/["']/g, '')           // strip quotes
      .replace(/\.{2,}$/,'')          // strip trailing ...
      .trim();
    if (qLower.length < 5) return null;

    // 1. Try exact match
    const exactIdx = tLower.indexOf(qLower);
    if (exactIdx !== -1) return { start: exactIdx, length: qLower.length };

    // 2. If quote has "..." in middle, split into segments and search each
    if (qLower.includes('...') || qLower.includes('…')) {
      const segments = qLower.split(/\.{2,}|…/).map(s => s.trim()).filter(s => s.length >= 4);
      for (const seg of segments) {
        const segWords = seg.split(/\s+/);
        for (let len = segWords.length; len >= 3; len--) {
          const sub = segWords.slice(0, len).join(' ');
          const idx = tLower.indexOf(sub);
          if (idx !== -1) {
            const sentEnd = transcript.indexOf('\n', idx + sub.length);
            const matchLen = sentEnd !== -1 ? Math.min(sentEnd - idx, sub.length + 60) : sub.length;
            return { start: idx, length: matchLen };
          }
        }
      }
    }

    // 3. Try progressively shorter prefixes
    const words = qLower.split(/\s+/);
    for (let len = words.length; len >= 3; len--) {
      const sub = words.slice(0, len).join(' ');
      const idx = tLower.indexOf(sub);
      if (idx !== -1) {
        const sentEnd = transcript.indexOf('\n', idx + sub.length);
        const matchLen = sentEnd !== -1 ? Math.min(sentEnd - idx, sub.length + 60) : sub.length;
        return { start: idx, length: matchLen };
      }
    }

    // 4. Try middle words (skip paraphrased prefix)
    for (let s = 1; s <= Math.min(3, words.length - 3); s++) {
      for (let len = Math.min(words.length - s, 6); len >= 3; len--) {
        const sub = words.slice(s, s + len).join(' ');
        const idx = tLower.indexOf(sub);
        if (idx !== -1) return { start: Math.max(0, idx - 10), length: sub.length + 20 };
      }
    }

    // 5. Try individual key phrases (2-3 word combos) for very short matches
    for (let i = 0; i < words.length - 1; i++) {
      const pair = words.slice(i, i + 2).join(' ');
      if (pair.length >= 6) {
        const idx = tLower.indexOf(pair);
        if (idx !== -1) return { start: Math.max(0, idx - 15), length: pair.length + 30 };
      }
    }

    return null;
  };

  const getScoreBg = (score) => {
    if (score >= 86) return 'bg-orange-500';
    if (score >= 71) return 'bg-success';
    if (score >= 51) return 'bg-warning';
    return 'bg-destructive';
  };

  const getClassificationBadge = (grade) => {
    const styles = {
      'Hot Deal': 'bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-600 border border-orange-500/30',
      'SQL': 'badge-sql',
      'Warm Lead': 'bg-yellow-500/15 text-amber-600 border border-yellow-500/30',
      'Cold Lead': 'bg-blue-500/15 text-blue-600 border border-blue-500/30',
      'Disqualified': 'bg-destructive/15 text-destructive border border-destructive/30'
    };
    return styles[grade] || 'bg-secondary text-muted-foreground';
  };

  const getRiskBadge = (status) => {
    const styles = {
      'Critical': 'bg-destructive text-white',
      'High': 'bg-destructive/80 text-white',
      'Medium': 'bg-warning/80 text-white',
      'Low': 'bg-success/20 text-success border border-success/30'
    };
    return styles[status] || styles['Low'];
  };

  const getDealHealthColor = (grade) => {
    const colors = {
      'Hot Deal': 'text-orange-600',
      'SQL': 'text-success',
      'Warm Lead': 'text-amber-600',
      'Cold Lead': 'text-blue-600',
      'Disqualified': 'text-destructive'
    };
    return colors[grade] || 'text-muted-foreground';
  };

  const getRecommendationColor = (rec) => {
    if (rec === 'Advance') return 'bg-success text-white';
    if (rec === 'Nurture') return 'bg-primary text-white';
    return 'bg-destructive text-white';
  };

  const getSignalIcon = (type) => {
    const icons = {
      'buying_intent': Zap,
      'authority': Shield,
      'budget': DollarSign,
      'timeline': Clock,
      'pain': AlertCircle,
      'risk': AlertTriangle
    };
    return icons[type] || Sparkles;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="cyber-spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Report not found</p>
        </div>
      </Layout>
    );
  }

  const { call, analysis, rep_info } = data;
  const qualScores = analysis.qualification_scores || {};
  const buyerProfile = analysis.buyer_profile || {};
  const coaching = analysis.coaching_insights || {};
  const sentiment = analysis.sentiment_analysis || {};
  const riskCategories = analysis.risk_categories || [];
  const signals = analysis.detected_signals || [];
  const nextQuestions = analysis.next_questions || [];
  const dealValue = analysis.estimated_deal_value || {};

  // Extract competitor mentions from risk categories and signals
  const getCompetitorMentions = () => {
    const mentions = [];
    const competitionRisk = riskCategories.find(r => r.category === 'competition');
    if (competitionRisk?.evidence?.length) {
      competitionRisk.evidence.forEach(ev => {
        // Extract the quoted part for transcript matching
        const quoteMatch = ev.match(/"([^"]+)"/);
        mentions.push({ text: ev, quote: quoteMatch ? quoteMatch[1] : ev, source: 'risk' });
      });
    }
    signals.forEach(s => {
      if (s.signal_category?.toLowerCase().includes('competitor') || s.signal_category?.toLowerCase().includes('competition')) {
        mentions.push({ text: s.evidence_quote, quote: s.evidence_quote, source: 'signal', strength: s.signal_strength });
      }
    });
    return mentions;
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="report-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              {call.title}
            </h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {call.company_name && (
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {call.company_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDate(call.call_date || call.created_at)}
              </span>
              {call.duration_seconds > 0 && (
                <span className="flex items-center gap-1">
                  <Activity className="w-4 h-4" />
                  {formatDuration(call.duration_seconds)}
                </span>
              )}
              {rep_info && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {rep_info.name}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            {/* Win Probability */}
            {(() => {
              const wp = getWinProbability(analysis.final_deal_score);
              const factors = getWinFactors();
              return (
                <div className="flex flex-col items-end gap-2" data-testid="win-probability-section">
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Win Probability</p>
                      <p className={`font-heading text-4xl font-bold ${wp.color}`} data-testid="deal-score">
                        {wp.pct}%
                      </p>
                    </div>
                    <Badge className={`${wp.bg} border text-sm px-3 py-1.5`} data-testid="classification-badge">
                      {wp.label}
                    </Badge>
                  </div>
                  <Badge className={getRecommendationColor(analysis.deal_recommendation)} data-testid="deal-recommendation">
                    {analysis.deal_recommendation}
                  </Badge>
                  {factors.length > 0 && (
                    <div className="mt-1">
                      <button
                        onClick={() => setDriversOpen(!driversOpen)}
                        className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 ml-auto hover:text-foreground transition-colors"
                        data-testid="based-on-toggle"
                      >
                        Based on {driversOpen ? '▼' : '▶'}
                      </button>
                      {driversOpen && (
                        <div className="space-y-1 mt-1 max-w-sm ml-auto">
                          {factors.map((f, i) => (
                            <p key={`r1-${i}`} className="text-xs text-muted-foreground flex items-start justify-end gap-1.5 text-right leading-snug">
                              <span className={`shrink-0 mt-0.5 ${f.positive ? 'text-emerald-600' : 'text-red-600'}`}>{f.positive ? '✓' : '✗'}</span>
                              <span className="line-clamp-2">{f.text}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Deal Value */}
            <div className="flex items-center gap-2" data-testid="deal-value-section">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              {editingDealValue ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">$</span>
                  <input
                    type="text"
                    value={dealValueInput}
                    onChange={(e) => setDealValueInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveDealValue(); if (e.key === 'Escape') setEditingDealValue(false); }}
                    className="w-28 h-7 px-2 text-sm bg-background border border-border rounded text-foreground"
                    placeholder="0"
                    autoFocus
                    data-testid="deal-value-input"
                  />
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={saveDealValue} data-testid="deal-value-save">Save</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setEditingDealValue(false)}>Cancel</Button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-1.5 text-sm hover:text-primary transition-colors group"
                  onClick={() => {
                    setDealValueInput(dealValue.amount ? String(dealValue.amount) : '');
                    setEditingDealValue(true);
                  }}
                  data-testid="deal-value-edit-btn"
                >
                  <span className="font-semibold text-foreground">
                    {dealValue.amount ? `$${Number(dealValue.amount).toLocaleString()}` : 'Set value'}
                  </span>
                  {dealValue.confidence && dealValue.amount && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 border-border/50">
                      {dealValue.confidence}
                    </Badge>
                  )}
                  <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={linkToDeal}
                disabled={linkingDeal}
                data-testid="link-to-deal-btn"
              >
                {linkingDeal ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Building className="w-3 h-3 mr-1" />}
                Link to Deal
              </Button>
              {!call.archived && (
              <Select onValueChange={handleArchive} disabled={archiving}>
                <SelectTrigger className="w-40 h-8 text-xs bg-card border-border">
                  <Archive className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="Archive as..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="won">Won Deal</SelectItem>
                  <SelectItem value="lost">Lost Deal</SelectItem>
                  <SelectItem value="disqualified">Disqualified</SelectItem>
                  <SelectItem value="old">Old Call</SelectItem>
                </SelectContent>
              </Select>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs text-red-600 border-red-500/30 hover:bg-red-500/10 hover:text-red-600"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-call-btn"
              >
                {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Smart Insights (replaces Executive Summary) */}
        <div className="space-y-4" data-testid="smart-insights-section">
          {!insights ? (
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    {loadingInsights ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <Sparkles className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {loadingInsights ? 'Generating Smart Insights...' : 'Executive Summary'}
                    </h3>
                    {loadingInsights ? (
                      <p className="text-sm text-muted-foreground">Analyzing call data and building actionable insights...</p>
                    ) : (
                      <>
                        <p className="text-muted-foreground leading-relaxed mb-3">
                          {analysis.call_summary || 'No summary available'}
                        </p>
                        <Button size="sm" variant="outline" onClick={generateInsights} disabled={loadingInsights} data-testid="generate-insights-btn">
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          Generate Smart Insights
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Headline + Description */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="w-4.5 h-4.5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground" data-testid="insights-headline">
                          {insights.summary?.headline}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          {insights.summary?.description}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={generateInsights} disabled={loadingInsights} data-testid="regenerate-insights-btn">
                      {loadingInsights ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Key Risks - always visible */}
              <Card className="bg-card border-red-500/15">
                <CardContent className="p-4 space-y-3">
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Key Risks</p>
                  {insights.key_risks?.map((r, i) => (
                    <div key={`r2-${i}`} className="flex items-start gap-2 p-2.5 rounded-lg bg-red-500/5 border border-red-500/10">
                      <Shield className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                      <span className="text-xs text-foreground/85">{r}</span>
                    </div>
                  ))}
                  {(!insights.key_risks || insights.key_risks.length === 0) && (
                    <p className="text-xs text-muted-foreground">No critical risks identified</p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="qualification" className="w-full">
          <TabsList className="w-full justify-start bg-card border border-border overflow-x-auto flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="qualification" className="data-[state=active]:bg-primary/10">
              <Target className="w-4 h-4 mr-2" />
              Qualification
            </TabsTrigger>
            {hasFeature('signal_detection') && (
              <TabsTrigger value="signals" className="data-[state=active]:bg-primary/10">
                <Zap className="w-4 h-4 mr-2" />
                Signals
              </TabsTrigger>
            )}
            {hasFeature('objection_intelligence') && (
              <TabsTrigger value="objections" className="data-[state=active]:bg-primary/10">
                <MessageSquare className="w-4 h-4 mr-2" />
                Objections
              </TabsTrigger>
            )}
            {hasFeature('coaching') && (
              <TabsTrigger value="coaching" className="data-[state=active]:bg-primary/10">
                <Brain className="w-4 h-4 mr-2" />
                Coaching
              </TabsTrigger>
            )}
            {hasFeature('next_question_generator') && (
              <TabsTrigger value="questions" className="data-[state=active]:bg-primary/10">
                <HelpCircle className="w-4 h-4 mr-2" />
                Next Questions
              </TabsTrigger>
            )}
            {hasFeature('follow_up_email') && (
              <TabsTrigger value="email" className="data-[state=active]:bg-primary/10">
                <Mail className="w-4 h-4 mr-2" />
                Follow-up
              </TabsTrigger>
            )}
          </TabsList>

          {/* Upgrade Banner for locked features */}
          {!hasFeature('coaching') && (
            <div className="mt-4 flex items-center gap-3 p-4 rounded-lg bg-secondary/50 border border-border/50" data-testid="report-upgrade-banner">
              <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Some analysis features are locked</p>
                <p className="text-xs text-muted-foreground">Upgrade to Pro or Team to unlock Coaching, Risk Radar, AI Questions, and more.</p>
              </div>
              <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary text-primary-foreground">
                Upgrade
              </Button>
            </div>
          )}

          {/* Signals Tab */}
          {hasFeature('signal_detection') && (
          <TabsContent value="signals" className="mt-6 space-y-6">
            {/* Probability Drivers from Smart Insights */}
            {insights && insights.why_this_probability && (
              <Card className="bg-card border-border/50">
                <CardContent className="p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Probability Drivers</p>
                  {insights.why_this_probability.positive_drivers?.length > 0 && (
                    <div className="space-y-1.5">
                      {insights.why_this_probability.positive_drivers.map((d, i) => (
                        <div key={`r3-${i}`} className="flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          <span className="text-xs text-foreground/85">{d}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {insights.why_this_probability.negative_drivers?.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      {insights.why_this_probability.negative_drivers.map((d, i) => (
                        <div key={`r4-${i}`} className="flex items-start gap-2">
                          <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                          <span className="text-xs text-foreground/85">{d}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {insights.why_this_probability.missing_elements?.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                      {insights.why_this_probability.missing_elements.map((d, i) => (
                        <div key={`r5-${i}`} className="flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                          <span className="text-xs text-foreground/85">{d}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI Task Recommendations */}
            {taskRecs.length > 0 && (
              <Card className="bg-primary/[0.03] border-primary/20" data-testid="task-recommendations">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Recommended Tasks
                  </p>
                  <div className="space-y-2">
                    {taskRecs.map((rec, i) => (
                      <div key={`r6-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-card border border-border/50">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">{rec.title}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {rec.company} &middot; Due {new Date(rec.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
                          onClick={() => acceptTask(rec)}
                          disabled={creatingTask === rec.title}
                          data-testid={`accept-task-${i}`}
                        >
                          {creatingTask === rec.title ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Accept'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deal Risk Radar */}
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Radar className="w-4 h-4 text-destructive" />
                  Deal Risk Radar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 grid-cols-5">
                  {['budget', 'authority', 'timeline', 'competition', 'engagement'].map(category => {
                    const riskData = riskCategories.find(r => r.category === category) || { score: 0 };
                    const score = riskData.score || 0;
                    return (
                      <div key={category} className="text-center">
                        <div className="relative w-20 h-20 mx-auto mb-1.5">
                          <svg className="w-full h-full transform -rotate-90">
                            <circle cx="40" cy="40" r="32" fill="none" stroke="hsl(var(--secondary))" strokeWidth="6" />
                            <circle cx="40" cy="40" r="32" fill="none"
                              stroke={score >= 60 ? 'hsl(var(--destructive))' : score >= 30 ? 'hsl(var(--warning))' : 'hsl(var(--success))'}
                              strokeWidth="6" strokeDasharray={`${score * 2.01} 201`} strokeLinecap="round" />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`font-heading text-lg font-bold ${score >= 60 ? 'text-destructive' : score >= 30 ? 'text-warning' : 'text-success'}`}>{score}</span>
                          </div>
                        </div>
                        <p className="text-xs font-medium capitalize">{category}</p>
                        <p className="text-[10px] text-muted-foreground">{score >= 60 ? 'High' : score >= 30 ? 'Medium' : 'Low'}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-3 rounded-lg bg-secondary/30 border border-border">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium">Overall Risk</span>
                    <Badge className={getRiskBadge(analysis.risk_status)}>{analysis.risk_status} ({analysis.overall_risk_score})</Badge>
                  </div>
                  <Progress value={analysis.overall_risk_score} className={`h-2.5 ${analysis.overall_risk_score >= 60 ? 'bg-destructive/20' : analysis.overall_risk_score >= 30 ? 'bg-warning/20' : 'bg-success/20'}`} />
                </div>
              </CardContent>
            </Card>

            {analysis.risk_indicators?.length > 0 && (
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-warning" />
                    Risk Indicators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {analysis.risk_indicators.map((risk, idx) => (
                    <div key={`r1-${idx}`} className="p-2.5 rounded-lg bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getRiskBadge(risk.severity)} variant="outline">{risk.severity}</Badge>
                        <span className="text-xs font-medium">{risk.risk}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{risk.details}</p>
                      {risk.evidence && <p className="text-[10px] text-muted-foreground italic mt-1">"{risk.evidence}"</p>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Competitors Mentioned */}
            {(() => {
              const competitors = getCompetitorMentions();
              if (competitors.length === 0) return null;
              return (
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-orange-600" />
                      Competitors Mentioned
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {competitors.map((c, idx) => {
                      const isActive = activeSignal === c.quote;
                      return (
                        <button
                          key={`r2-${idx}`}
                          onClick={() => setActiveSignal(isActive ? null : c.quote)}
                          className={`w-full text-left p-3 rounded-lg border transition-all cursor-pointer ${
                            isActive
                              ? 'bg-orange-500/10 border-orange-500/40 ring-1 ring-orange-500/30'
                              : 'bg-secondary/30 border-border/50 hover:border-orange-500/30'
                          }`}
                          data-testid={`competitor-btn-${idx}`}
                        >
                          <div className="flex items-start gap-2">
                            <Users className="w-3.5 h-3.5 text-orange-600 mt-0.5 shrink-0" />
                            <p className="text-xs text-foreground/85 italic">"{c.text}"</p>
                          </div>
                          {c.strength && (
                            <span className="text-[10px] text-muted-foreground mt-1 block pl-5">Signal strength: {c.strength}/10</span>
                          )}
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Split: Signals left + Transcript right */}
            {signals.length > 0 ? (
              <div className="grid gap-4 lg:grid-cols-5" data-testid="signals-transcript-split">
                {/* Signals - Left Panel */}
                <div className="lg:col-span-2 space-y-3">
                  {['buying_intent', 'authority', 'budget', 'timeline', 'pain', 'risk'].map(signalType => {
                    const typeSignals = signals.filter(s => s.signal_type === signalType);
                    if (typeSignals.length === 0) return null;
                    const SignalIcon = getSignalIcon(signalType);
                    const isRisk = signalType === 'risk';
                    return (
                      <div key={signalType}>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-2">
                          <SignalIcon className={`w-3.5 h-3.5 ${isRisk ? 'text-destructive' : 'text-primary'}`} />
                          {signalType.replace('_', ' ')} ({typeSignals.length})
                        </p>
                        {typeSignals.map((signal, idx) => {
                          const isActive = activeSignal === signal.evidence_quote;
                          return (
                            <button
                              key={`r3-${idx}`}
                              onClick={() => setActiveSignal(isActive ? null : signal.evidence_quote)}
                              className={`w-full text-left p-3 rounded-lg border mb-2 transition-all cursor-pointer ${
                                isActive
                                  ? 'bg-primary/10 border-primary/40 ring-1 ring-primary/30'
                                  : 'bg-secondary/30 border-border/50 hover:border-primary/30'
                              }`}
                              data-testid={`signal-btn-${signalType}-${idx}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <Badge variant="outline" className="text-[10px] capitalize">
                                  {signal.signal_category}
                                </Badge>
                                <span className={`text-[10px] font-medium ${
                                  signal.signal_strength >= 7 ? 'text-success' :
                                  signal.signal_strength >= 4 ? 'text-warning' : 'text-destructive'
                                }`}>
                                  {signal.signal_strength}/10
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground italic line-clamp-2">
                                "{signal.evidence_quote}"
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Transcript - Right Panel */}
                <Card className="lg:col-span-3 bg-card border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      Transcript
                      {activeSignal && (
                        <Badge variant="outline" className="text-[10px] ml-2 text-primary border-primary/30">
                          Highlighting match
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[500px] rounded-lg border border-border p-4" data-testid="signals-transcript-panel">
                      {call.transcript_structured?.length > 0 ? (
                        <div className="space-y-4">
                          {call.transcript_structured.map((entry, idx) => {
                            const match = activeSignal ? findBestMatch(entry.text || '', activeSignal) : null;
                            return (
                              <div key={`r4-${idx}`} className={`flex gap-4 rounded-lg p-2 transition-colors ${match ? 'bg-primary/10 ring-1 ring-primary/30' : ''}`}>
                                <div className="shrink-0 w-20 text-xs text-muted-foreground font-mono">{entry.timestamp}</div>
                                <div className="flex-1">
                                  <span className={`text-xs font-medium ${entry.speaker === 'Sales Rep' ? 'text-primary' : 'text-warning'}`}>
                                    {entry.speaker}
                                  </span>
                                  <p className="text-sm text-foreground mt-1">
                                    {match ? (
                                      <>
                                        {entry.text.slice(0, match.start)}
                                        <mark className="bg-primary/30 text-foreground rounded px-0.5">{entry.text.slice(match.start, match.start + match.length)}</mark>
                                        {entry.text.slice(match.start + match.length)}
                                      </>
                                    ) : entry.text}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="font-mono text-sm whitespace-pre-wrap text-muted-foreground" data-testid="transcript-raw">
                          {activeSignal && call.transcript_raw ? (() => {
                            const raw = call.transcript_raw;
                            const match = findBestMatch(raw, activeSignal);
                            if (!match) return raw;
                            return (
                              <>
                                {raw.slice(0, match.start)}
                                <mark className="bg-primary/30 text-foreground rounded px-0.5">{raw.slice(match.start, match.start + match.length)}</mark>
                                {raw.slice(match.start + match.length)}
                              </>
                            );
                          })() : (call.transcript_raw || 'No transcript available')}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-card border-border/50">
                <CardContent className="p-8 text-center">
                  <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No signals detected in this call</p>
                </CardContent>
              </Card>
            )}

          </TabsContent>
          )}

          {/* Qualification Tab */}
          <TabsContent value="qualification" className="mt-6 space-y-6">
            {hasFeature('scoring') ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[
                { key: 'intent', label: 'Intent to Buy', icon: TrendingUp, weight: '25%', context: buyerProfile.intent_level },
                { key: 'authority', label: 'Authority', icon: Shield, weight: '15%', context: buyerProfile.role },
                { key: 'budget', label: 'Budget', icon: DollarSign, weight: '20%', context: buyerProfile.budget_signals },
                { key: 'timeline', label: 'Timeline', icon: Clock, weight: '15%', context: buyerProfile.timeline },
                { key: 'icp_fit', label: 'ICP Fit', icon: Users, weight: '15%', context: null },
                { key: 'risk', label: 'Risk (higher=safer)', icon: AlertTriangle, weight: '10%', context: null }
              ].map(({ key, label, icon: Icon, weight, context }) => {
                const v3 = analysis.scoring_v3 || {};
                const reasoning = analysis.scoring_reasoning || {};
                const score = v3[key] ?? 0;
                const scoreColor = score >= 4 ? 'text-success' : score >= 3 ? 'text-amber-600' : score >= 2 ? 'text-warning' : 'text-destructive';
                const barColor = score >= 4 ? 'bg-success' : score >= 3 ? 'bg-yellow-500' : score >= 2 ? 'bg-warning' : 'bg-destructive';
                return (
                  <Card key={key} className="bg-card border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-primary" />
                          <span className="font-medium text-foreground">{label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{weight}</span>
                          <span className={`font-heading text-2xl font-bold ${scoreColor}`}>
                            {score}/5
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={`r7-${i}`} className={`flex-1 h-2 rounded-full ${i <= score ? barColor : 'bg-secondary'}`} />
                        ))}
                      </div>
                      {reasoning[key] && (
                        <p className="text-xs text-muted-foreground leading-relaxed mb-1">{reasoning[key]}</p>
                      )}
                      {context && (
                        <p className="text-xs text-muted-foreground/70 italic truncate">{context}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            ) : (
            <Card className="bg-card border-border/50">
              <CardContent className="p-8 text-center">
                <Lock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">Detailed Scoring Locked</h3>
                <p className="text-muted-foreground text-sm mb-4">Upgrade to Starter or higher to see detailed qualification scoring breakdown.</p>
                <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary text-primary-foreground">
                  View Plans
                </Button>
              </CardContent>
            </Card>
            )}

            {/* Buyer Profile */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Buyer Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Company</p>
                    <p className="font-medium">{buyerProfile.company_name || call.company_name || 'Not mentioned'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Industry</p>
                    <p className="font-medium">{buyerProfile.industry || 'Not mentioned'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Role</p>
                    <p className="font-medium">{buyerProfile.role || 'Not detected'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Sentiment</p>
                    <p className="font-medium">{sentiment.buyer_sentiment || 'Unknown'} / {sentiment.tone || 'Unknown'}</p>
                  </div>
                </div>
                
                {buyerProfile.pain_points?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Pain Points</p>
                    <div className="flex flex-wrap gap-2">
                      {buyerProfile.pain_points.map((pain, idx) => (
                        <Badge key={`r5-${idx}`} variant="outline" className="border-warning/30 text-warning">
                          {pain}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Objections Tab */}
          {hasFeature('objection_intelligence') && (
          <TabsContent value="objections" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Objection Intelligence ({analysis.objections?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analysis.objections?.length > 0 ? (
                  <div className="space-y-4">
                    {analysis.objections.map((obj, idx) => (
                      <div key={`r6-${idx}`} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{obj.objection_type}</Badge>
                            <Badge className={
                              obj.severity >= 7 ? 'bg-destructive/15 text-destructive' :
                              obj.severity >= 4 ? 'bg-warning/15 text-warning' : 
                              'bg-muted text-muted-foreground'
                            }>
                              Severity: {obj.severity}/10
                            </Badge>
                          </div>
                          <Badge className={
                            obj.rep_response_quality === 'Excellent' ? 'badge-sql' :
                            obj.rep_response_quality === 'Good' ? 'badge-mql' :
                            obj.rep_response_quality === 'Fair' ? 'bg-warning/15 text-warning' :
                            'badge-non-icp'
                          }>
                            {obj.rep_response_quality} Response
                          </Badge>
                        </div>
                        <p className="font-medium text-foreground mb-2">{obj.buyer_concern}</p>
                        <p className="text-sm text-muted-foreground italic mb-3">"{obj.evidence_quote}"</p>
                        {obj.coaching_tip && (
                          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                            <p className="text-xs text-primary font-medium mb-1">Coaching Tip:</p>
                            <p className="text-sm text-foreground">{obj.coaching_tip}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No objections detected</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Coaching Tab */}
          {hasFeature('coaching') && (
          <TabsContent value="coaching" className="mt-6 space-y-6">
            {/* Rep Performance from Smart Insights */}
            {insights && insights.performance?.length > 0 && (
              <Card className="bg-card border-border/50" data-testid="performance-card">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Rep Performance</p>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {insights.performance.map((p, i) => {
                      const statusColor = p.status === 'Good' ? 'border-emerald-500/30 bg-emerald-500/5' :
                        p.status === 'Poor' ? 'border-red-500/30 bg-red-500/5' : 'border-yellow-500/30 bg-yellow-500/5';
                      const statusText = p.status === 'Good' ? 'text-emerald-600' :
                        p.status === 'Poor' ? 'text-red-600' : 'text-amber-600';
                      return (
                        <div key={`r8-${i}`} className={`p-3 rounded-lg border ${statusColor}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold text-foreground">{p.area}</span>
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusText} border-current/30`}>{p.status}</Badge>
                          </div>
                          <p className="text-xs text-foreground/80">{p.insight}</p>
                          <p className="text-[10px] text-muted-foreground mt-1">{p.impact}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {/* Discovery Quality */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-foreground">Discovery Quality</span>
                    <span className={`font-heading text-2xl font-bold ${getScoreColor(coaching.discovery_quality?.score || 0)}`}>
                      {coaching.discovery_quality?.score || 0}
                    </span>
                  </div>
                  <Progress value={coaching.discovery_quality?.score || 0} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{coaching.discovery_quality?.feedback || 'No feedback'}</p>
                </CardContent>
              </Card>

              {/* Question Depth */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-foreground">Question Depth</span>
                    <span className={`font-heading text-2xl font-bold ${getScoreColor(coaching.question_depth?.score || 0)}`}>
                      {coaching.question_depth?.score || 0}
                    </span>
                  </div>
                  <Progress value={coaching.question_depth?.score || 0} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{coaching.question_depth?.feedback || 'No feedback'}</p>
                </CardContent>
              </Card>

              {/* Objection Handling */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-foreground">Objection Handling</span>
                    <span className={`font-heading text-2xl font-bold ${getScoreColor(coaching.objection_handling?.score || 0)}`}>
                      {coaching.objection_handling?.score || 0}
                    </span>
                  </div>
                  <Progress value={coaching.objection_handling?.score || 0} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{coaching.objection_handling?.feedback || 'No feedback'}</p>
                </CardContent>
              </Card>

              {/* Talk Ratio */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-foreground">Talk Ratio (Rep)</span>
                    <span className={`font-heading text-2xl font-bold ${
                      (coaching.talk_ratio?.rep_percentage || 50) <= 40 ? 'text-success' :
                      (coaching.talk_ratio?.rep_percentage || 50) <= 50 ? 'text-warning' : 'text-destructive'
                    }`}>
                      {coaching.talk_ratio?.rep_percentage || 50}%
                    </span>
                  </div>
                  <Progress value={coaching.talk_ratio?.rep_percentage || 50} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{coaching.talk_ratio?.feedback || 'Ideal: 30-40% rep talking'}</p>
                </CardContent>
              </Card>

              {/* Next Step Clarity */}
              <Card className="bg-card border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-foreground">Next Step Clarity</span>
                    <span className={`font-heading text-2xl font-bold ${getScoreColor(coaching.next_step_clarity?.score || 0)}`}>
                      {coaching.next_step_clarity?.score || 0}
                    </span>
                  </div>
                  <Progress value={coaching.next_step_clarity?.score || 0} className="h-2 mb-2" />
                  <p className="text-xs text-muted-foreground">{coaching.next_step_clarity?.feedback || 'No feedback'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Coaching Suggestions */}
            {coaching.discovery_quality?.suggestions?.length > 0 && (
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Improvement Suggestions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {[
                      ...(coaching.discovery_quality?.suggestions || []),
                      ...(coaching.question_depth?.suggestions || []),
                      ...(coaching.objection_handling?.suggestions || []),
                      ...(coaching.next_step_clarity?.suggestions || [])
                    ].slice(0, 5).map((suggestion, idx) => (
                      <li key={`r7-${idx}`} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <span className="text-sm text-muted-foreground">{suggestion}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Recommended Actions */}
            {analysis.recommended_rep_actions?.length > 0 && (
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Recommended Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {analysis.recommended_rep_actions.map((action, idx) => (
                      <li key={`r8-${idx}`} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary">{idx + 1}</span>
                        </div>
                        <span className="text-sm text-foreground">{action}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          )}

          {/* Next Questions Tab */}
          {hasFeature('next_question_generator') && (
          <TabsContent value="questions" className="mt-6 space-y-6">
            {/* Next Actions from Smart Insights */}
            {insights && insights.next_actions?.length > 0 && (
              <Card className="bg-card border-primary/15">
                <CardContent className="p-4 space-y-3">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider">Next Actions</p>
                  {insights.next_actions.map((a, i) => (
                    <div key={`r9-${i}`} className="flex items-start gap-2">
                      <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                      <span className="text-xs text-foreground/85">{a}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Recommended Questions from Smart Insights */}
            {insights && (insights.recommended_questions?.must_ask?.length > 0 || insights.recommended_questions?.should_ask?.length > 0) && (
              <Card className="bg-card border-border/50" data-testid="recommended-questions-card">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">AI Recommended Questions</p>
                  <div className="grid gap-4 md:grid-cols-2">
                    {insights.recommended_questions.must_ask?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-red-600 mb-2">Must Ask</p>
                        <div className="space-y-1.5">
                          {insights.recommended_questions.must_ask.map((q, i) => (
                            <div key={`r10-${i}`} className="flex items-start gap-2 p-2 rounded bg-red-500/5 border border-red-500/10">
                              <HelpCircle className="w-3 h-3 text-red-600 mt-0.5 shrink-0" />
                              <span className="text-xs text-foreground/85">{q}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {insights.recommended_questions.should_ask?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Should Ask</p>
                        <div className="space-y-1.5">
                          {insights.recommended_questions.should_ask.map((q, i) => (
                            <div key={`r11-${i}`} className="flex items-start gap-2 p-2 rounded bg-secondary/30 border border-border/50">
                              <HelpCircle className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                              <span className="text-xs text-foreground/85">{q}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  Recommended Questions for Next Conversation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {nextQuestions.length > 0 ? (
                  <div className="space-y-3">
                    {nextQuestions.map((q, idx) => (
                      <div key={`r9-${idx}`} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {q.category}
                          </Badge>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">Priority:</span>
                            {[...Array(q.priority || 3)].map((_, i) => (
                              <Zap key={`r12-${i}`} className="w-3 h-3 text-primary" />
                            ))}
                          </div>
                        </div>
                        <p className="text-foreground">{q.question}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No questions generated</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Follow-up Email Tab */}
          {hasFeature('follow_up_email') && (
          <TabsContent value="email" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Follow-up Email Draft
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyEmail}
                    className="border-border hover:bg-secondary"
                    data-testid="copy-email-btn"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2 text-success" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Email
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-background rounded-lg p-6 font-mono text-sm whitespace-pre-wrap border border-border">
                  {analysis.follow_up_email_draft || 'No follow-up email generated'}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Transcript Tab */}
        </Tabs>

        {/* Key Signals and Next Best Action moved into their respective tabs */}
      </div>
    </Layout>
  );
}
